import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../../store/authSlice';
import { Container, TextField, Button, Box, Typography, Alert, Tooltip, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import api from '../../utils/axiosConfig';
import { toast } from 'react-toastify';



const ResetPassword = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [password, setPassword] = useState('');
    const [sendLinkAgain, setSendLinkAgain] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const passwordValidationResult = validatePassword(password);
        if (passwordValidationResult !== true) {
            setError(passwordValidationResult);
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            console.error("Passwords don't match");
            return;
        }

        setSendLinkAgain(false);
        try {
            if (!token) {
                setError('Token expired');
                return;
            }
            const body = { password, confirmPassword };
            // Using custom headers for this specific request (overriding the global token)
            const response = await api.post(`/api/forgotPassword/reset`, body, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            setError('');
            const data = response.data;
            if (data.success) {
                toast.success('Password reset successful!');
                dispatch(logout());
                navigate('/login'); // Redirect to first login flow(Eula and reset password)
            } else {
                setError('Invalid login response. Please try again.');
            }
        } catch (error) {
            if (error.response?.data?.err) {
                if (error.response.data.err.includes('expired')) {
                    setSendLinkAgain(true);
                }
                setError(error.response.data.err);
            } else if (error.response && error.response.status === 401) {
                setError('Token may have expired. Please request a new password reset link.');
            } else if (error.response && error.response.status === 500) {
                setError('Server error. Please try again later.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        }
    };

    const handleResubmitPasswordRecovery = () => {
        if (!sendLinkAgain) {
            return;
        }
        navigate('/forgotPassword');
    }

    const validatePassword = (password) => {
        const lengthCheck = /^.{8,30}$/;
        const digitCheck = /\d/;
        const upperCaseCheck = /[A-Z]/;
        const lowerCaseCheck = /[a-z]/;
        const specialCharacterCheck = /[!@#$%^&*(),.?":{}|<>]/;
        let err = '';
        if (!lengthCheck.test(password)) {
            err += 'Password must be between 8 and 30 characters in length. ';
        }
        if (!digitCheck.test(password)) {
            err += 'Password must include at least one digit. ';
        }
        if (!upperCaseCheck.test(password) || !lowerCaseCheck.test(password)) {
            err += 'Password must include both upper and lower case characters. ';
        }
        if (!specialCharacterCheck.test(password)) {
            err += 'Password must include at least one special character.';
        }
        if (!err) {
            return true;  // Return true if all checks pass
        } else {
            return err;
        }
    };

    return (

        <Container maxWidth="xs">
            {token ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="90vh"
                >
                    <Box sx={{ position: 'relative' }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Reset Password
                        </Typography>

                        <Tooltip
                            title={
                                <div>
                                    <Typography variant="body1">Password Requirements:</Typography>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '16px' }}>
                                        <li>Password must be between 8 and 30 characters in length.</li>
                                        <li>Password must include at least one digit.</li>
                                        <li>Password must include both upper and lower case characters.</li>
                                        <li>Password must include at least one special character.</li>
                                    </ul>
                                </div>
                            }
                        >
                            <IconButton
                                color="primary"
                                sx={{
                                    position: 'absolute',
                                    right: '-50px',
                                    top: '1px',
                                }}>
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {error && <Alert onClick={handleResubmitPasswordRecovery} severity="error" sx={{ width: '100%', mb: 2, cursor: 'pointer' }}>{error}</Alert>}
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }} autoComplete="off">
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="password"
                            autoFocus
                            autoComplete="new-password"
                            value={password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}  // Toggle showConfirmPassword state
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            id="confirmPassword"
                            value={confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}  // Toggle showConfirmPassword state
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            Update Password
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="90vh"
                >
                    <Typography>Invalid URL. Please go to your email and click the link again.</Typography>
                </Box>
            )}
        </Container>
    );
};

export default ResetPassword;